/**
 * SF-5080: Lists (admin:priorities:manage). Add a list, rename it, describe it, and choose
 * whether it is open for voting, not open, or archived.
 *
 * SF-5087: every list is edited the same way, and an archived list can be deleted with all its
 * items, rankings and suggestions, once the manager types its name to confirm.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Card, Form, Input, Modal, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatShortDate } from '../../../utils/date';
import {
  createListArgs,
  deleteListArgs,
  prioritiesErrorMessage,
  setListStatusArgs,
  updateListArgs,
  usePriorityLists,
  usePrioritiesWrite,
  type ListStatus,
  type PriorityList,
} from '../api/prioritiesClient';
import { LIST_DESCRIPTION_MAX, LIST_NAME_MAX, LIST_STATUS_LABELS } from '../constants';
import { LoadError, RefreshWarning } from './LoadError';

const STATUS_OPTIONS = (Object.keys(LIST_STATUS_LABELS) as ListStatus[]).map((value) => ({
  value,
  label: LIST_STATUS_LABELS[value],
}));

interface ListValues {
  name: string;
  description?: string;
}

const ListFormModal: React.FC<{
  open: boolean;
  list: PriorityList | null;
  onSubmit: (values: { name: string; description: string }) => Promise<void>;
  onClose: () => void;
}> = ({ open, list, onSubmit, onClose }) => {
  const [form] = Form.useForm<ListValues>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    form.setFieldsValue({ name: list?.name ?? '', description: list?.description ?? '' });
  }, [open, list, form]);

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      await onSubmit({ name: values.name.trim(), description: values.description?.trim() ?? '' });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={list ? 'Edit list' : 'Add list'}
      okText={list ? 'Save' : 'Add list'}
      onOk={submit}
      confirmLoading={saving}
      onCancel={onClose}
      forceRender
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, whitespace: true, message: 'Give the list a name' },
            { max: LIST_NAME_MAX, message: `At most ${LIST_NAME_MAX} characters` },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: LIST_DESCRIPTION_MAX, message: `At most ${LIST_DESCRIPTION_MAX} characters` },
          ]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/**
 * `list` is read from the current lists by id, so the counts shown are the latest answer.
 * `unavailable`: that answer may be out of date (the last read failed, or a read is under way),
 * so the counts may be wrong and deleting waits. `failed`: the last read failed.
 */
const DeleteListModal: React.FC<{
  open: boolean;
  list: PriorityList | null;
  unavailable: boolean;
  failed: boolean;
  onDelete: (list: PriorityList) => Promise<void>;
  onClose: () => void;
}> = ({ open, list, unavailable, failed, onDelete, onClose }) => {
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listId = list?.id;
  useEffect(() => {
    setTyped('');
    setError(null);
  }, [open, listId]);
  const confirmed = !!list && !unavailable && typed.trim() === list.name;
  const submit = async () => {
    if (!list || !confirmed) return;
    setDeleting(true);
    try {
      await onDelete(list);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };
  return (
    <Modal
      open={open}
      title={list ? `Delete ${list.name}` : 'Delete list'}
      okText="Delete list"
      okButtonProps={{ danger: true, disabled: !confirmed }}
      onOk={submit}
      confirmLoading={deleting}
      onCancel={onClose}
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {unavailable && (
        <Alert
          type={failed ? 'warning' : 'info'}
          showIcon
          message={
            failed
              ? 'The lists could not be refreshed, so the counts below may be out of date. Deleting waits until they load.'
              : 'Refreshing the counts below. Deleting waits until they load.'
          }
          style={{ marginBottom: 12 }}
        />
      )}
      <p>
        This deletes the list for good, with its {list?.openItemCount ?? 0} open items, its closed
        items, everyone&apos;s rankings and its {list?.pendingSuggestionCount ?? 0} waiting
        suggestions. It cannot be undone.
      </p>
      <p>Type the list name to confirm.</p>
      <Input
        aria-label="List name to confirm"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={list?.name}
      />
    </Modal>
  );
};

export const ListsTab: React.FC = () => {
  const { message } = App.useApp();
  const listsQuery = usePriorityLists();
  const write = usePrioritiesWrite();
  const [editing, setEditing] = useState<PriorityList | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  // The list being deleted, by id: the window reads it from the current lists, never a copy.
  // The name is kept only to say what happened if the list goes.
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const lists = listsQuery.data?.data?.lists ?? [];
  // Deleting goes by the counts and status shown, so it waits while they may be out of date:
  // the last read failed, or any read (a refresh or a retry) is under way.
  const unavailable = listsQuery.isError || listsQuery.isFetching;
  const deletingList = deleting ? (lists.find((l) => l.id === deleting.id) ?? null) : null;
  // Our own delete removes the list too; that is not news.
  const removing = useRef(false);
  const deletingGone = !!deleting && !unavailable && deletingList?.status !== 'archived';
  useEffect(() => {
    if (!deletingGone || !deleting || removing.current) return;
    setDeleting(null);
    void message.warning(
      deletingList
        ? `${deleting.name} is no longer archived, so it was not deleted.`
        : `${deleting.name} no longer exists.`,
    );
  }, [deletingGone, deleting, deletingList, message]);

  const setStatus = async (list: PriorityList, status: ListStatus) => {
    try {
      await write.run(setListStatusArgs(list.id, status));
      void message.success(`${list.name} is now ${LIST_STATUS_LABELS[status].toLowerCase()}`);
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'The list could not be changed.'));
    }
  };

  const deleteList = async (list: PriorityList) => {
    removing.current = true;
    try {
      await write.run(deleteListArgs(list.id));
      setDeleting(null);
      void message.success(`${list.name} was deleted`);
    } catch (error) {
      throw new Error(prioritiesErrorMessage(error, 'The list could not be deleted.'));
    } finally {
      removing.current = false;
    }
  };

  const submit = async (values: { name: string; description: string }) => {
    try {
      await write.run(editing ? updateListArgs(editing.id, values) : createListArgs(values));
    } catch (error) {
      throw new Error(prioritiesErrorMessage(error, 'The list could not be saved.'));
    }
  };

  const columns: ColumnsType<PriorityList> = [
    { title: 'List', dataIndex: 'name' },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Open items', dataIndex: 'openItemCount', width: 110 },
    {
      title: 'Waiting suggestions',
      dataIndex: 'pendingSuggestionCount',
      width: 170,
      render: (count?: number) => count ?? 0,
    },
    { title: 'Added', dataIndex: 'createdAt', width: 140, render: formatShortDate },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, list) => (
        <Select
          aria-label={`Status of ${list.name}`}
          style={{ width: 160 }}
          value={list.status}
          options={STATUS_OPTIONS}
          onChange={(status: ListStatus) => setStatus(list, status)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      render: (_, list) => (
        <Space size={4}>
          <Button
            size="small"
            aria-label={`Edit ${list.name}`}
            onClick={() => {
              setEditing(list);
              setFormOpen(true);
            }}
          >
            Edit
          </Button>
          {list.status === 'archived' && (
            <Button
              size="small"
              danger
              aria-label={`Delete ${list.name}`}
              disabled={unavailable}
              onClick={() => setDeleting({ id: list.id, name: list.name })}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (listsQuery.isError && !listsQuery.data) {
    return (
      <LoadError
        error={listsQuery.error}
        fallback="The lists could not be loaded."
        onRetry={listsQuery.refetch}
      />
    );
  }

  return (
    <Card
      size="small"
      title="Lists"
      extra={
        <Button
          type="primary"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          Add list
        </Button>
      }
    >
      {listsQuery.isError && (
        <RefreshWarning
          error={listsQuery.error}
          fallback="The lists could not be refreshed."
          onRetry={listsQuery.refetch}
        />
      )}
      <Table<PriorityList>
        rowKey="id"
        loading={listsQuery.isLoading}
        columns={columns}
        dataSource={lists}
        pagination={false}
        locale={{ emptyText: 'No lists yet' }}
      />
      <ListFormModal
        open={formOpen}
        list={editing}
        onSubmit={submit}
        onClose={() => setFormOpen(false)}
      />
      <DeleteListModal
        open={!!deleting}
        list={deletingList}
        unavailable={unavailable}
        failed={listsQuery.isError}
        onDelete={deleteList}
        onClose={() => setDeleting(null)}
      />
    </Card>
  );
};
